import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Avatar,
  Container,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushPinIcon from '@mui/icons-material/PushPin';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';

const NewsFeedCarousel = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPosts = useCallback(async () => {
    if (!currentFamily) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/family-posts/?family_id=${currentFamily.id}`);
      const allPosts = response.data.results || response.data;
      // Show pinned posts first, then recent posts (limit to 5 for carousel)
      const pinnedPosts = allPosts.filter(p => p.is_pinned);
      const regularPosts = allPosts.filter(p => !p.is_pinned);
      const sortedPosts = [...pinnedPosts, ...regularPosts].slice(0, 5);
      setPosts(sortedPosts);
    } catch (error) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchPosts();
    } else {
      setPosts([]);
    }
  }, [currentFamily, fetchPosts]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? posts.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === posts.length - 1 ? 0 : prev + 1));
  };

  const handlePostClick = () => {
    if (currentFamily) {
      navigate(`/families/${currentFamily.id}/feed`);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    if (posts.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === posts.length - 1 ? 0 : prev + 1));
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [posts.length]);

  // Don't show if no family
  if (!currentFamily) {
    return null;
  }

  // Show loading state or empty state
  if (loading) {
    return null; // Or return a loading spinner if desired
  }

  // Don't show if no posts
  if (posts.length === 0) {
    return null;
  }

  const currentPost = posts[currentIndex];

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mb: 4 }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 200, sm: 300, md: 350 },
          maxHeight: { xs: 300, sm: 400, md: 450 },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: { xs: 4, sm: 6, md: 8 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid rgba(255,255,255,0.2)',
          bgcolor: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
      <Card
        onClick={handlePostClick}
        sx={{
          cursor: 'pointer',
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: { xs: 200, sm: 300, md: 350 },
          maxHeight: { xs: 300, sm: 400, md: 450 },
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.3s',
          overflow: 'hidden',
          borderRadius: { xs: 4, sm: 6, md: 8 },
          boxShadow: 'none',
          '&:hover': {
            transform: 'scale(1.01)',
          },
        }}
      >
        {currentPost.image_url && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: { xs: 200, sm: 300, md: 350 },
              maxHeight: { xs: 300, sm: 400, md: 450 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.05)',
              overflow: 'hidden',
              p: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            <CardMedia
              component="img"
              image={currentPost.image_url}
              alt={currentPost.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              onLoad={() => {
              }}
              sx={{
                objectFit: 'contain',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                borderRadius: { xs: 3, sm: 4 },
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            />
          </Box>
        )}
        <CardContent
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: currentPost.image_url
              ? 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.3), transparent)'
              : 'transparent',
            position: currentPost.image_url ? 'absolute' : 'relative',
            bottom: 0,
            left: 0,
            right: 0,
            color: currentPost.image_url ? 'white' : 'inherit',
          }}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Avatar
                sx={{
                  width: { xs: 24, sm: 32 },
                  height: { xs: 24, sm: 32 },
                  bgcolor: currentPost.image_url ? 'rgba(255,255,255,0.3)' : 'primary.main',
                }}
              >
                {currentPost.user_name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  color: currentPost.image_url ? 'white' : 'text.secondary',
                }}
              >
                {currentPost.user_name}
              </Typography>
              {currentPost.is_pinned && (
                <PushPinIcon sx={{ fontSize: { xs: 14, sm: 18 }, color: currentPost.image_url ? 'white' : 'primary.main' }} />
              )}
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                fontWeight: 'bold',
                mb: 0.5,
                color: currentPost.image_url ? 'white' : 'inherit',
                textShadow: currentPost.image_url ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
              }}
              noWrap
            >
              {currentPost.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: currentPost.image_url ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                textShadow: currentPost.image_url ? '0 1px 2px rgba(0,0,0,0.5)' : 'none',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {currentPost.content}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Navigation arrows */}
      {posts.length > 1 && (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              zIndex: 2,
            }}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              zIndex: 2,
            }}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Dots indicator */}
      {posts.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
          }}
        >
          {posts.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.8)',
                },
              }}
            />
          ))}
        </Box>
      )}
      </Box>
    </Container>
  );
};

export default NewsFeedCarousel;
